import { createI18n } from 'vue-i18n';
import store from 'store2';
import type { Ref } from 'vue';
import { watch } from 'vue';
import roleNamesNL from '@/locales/i18n-role-names.nl.json';
import roleNamesEN from '@/locales/i18n-role-names.en.json';
import knownErrorNL from '@/locales/i18n-known-error.nl.json';
import knownErrorEN from '@/locales/i18n-known-error.en.json';
import globalsNL from '@/locales/i18n-globals.nl.json';
import globalsEN from '@/locales/i18n-globals.en.json';
import humanDurationNL from '@/locales/i18n-human-duration.nl.json';
import humanDurationEN from '@/locales/i18n-human-duration.en.json';
import rapportenNL from '@/locales/i18n-rapporten.nl.json';
import rapportenEN from '@/locales/i18n-rapporten.en.json';
import validatorMessagesNL from '@/locales/i18n-validation-messages.nl.json';
import validatorMessagesEN from '@/locales/i18n-validation-messages.en.json';
import vangnetNL from '@/locales/i18n-vangnet.nl.json';
import vangnetEN from '@/locales/i18n-vangnet.en.json';
import werknemerMessagesNL from '@/locales/i18n-werknemer.nl.json';
import werknemerMessagesEN from '@/locales/i18n-werknemer.en.json';
import { routeQuery } from '@/plugins/router';
import merge from '@/common/mergeDeep';

type Locale = 'nl' | 'en';
const DEFAULT_LOCALE: Locale = 'nl';

const getLocale = (): Locale => {
  if (import.meta.env.MODE === 'development') {
    const storedLocale = store.session.get('locale') as unknown;
    if (storedLocale && typeof storedLocale === 'string') {
      return storedLocale as Locale;
    }
  }
  return DEFAULT_LOCALE;
};

const euroCurrency = {
  style: 'currency',
  currency: 'EUR',
  currencyDisplay: 'symbol',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: true,
};

export const i18nSettings = {
  legacy: false,
  allowComposition: true,
  numberFormats: {
    en: {
      currency: euroCurrency,
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      },
      percent: {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: false,
      },
    },
    nl: {
      currency: euroCurrency,
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: true,
      },
      percent: {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: false,
      },
    },
  },
  datetimeFormats: {
    en: {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      },
      monthLong: {
        month: 'long',
      },
      monthDay: {
        month: 'short',
        day: '2-digit',
      },
      monthDayLong: {
        month: 'long',
        day: 'numeric',
      },
      monthDayYearLong: {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      },
      weekDayShort: {
        weekday: 'short',
      },
      weekDayAndDay: {
        weekday: 'short',
        day: 'numeric',
      },
      shortWithWeekDay: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
      },
      shortText: {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        weekday: 'short',
      },
      shortTextMonthYear: {
        year: 'numeric',
        month: 'short',
      },
      long: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      },
      longDate: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      },
      dateTime: {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      },
      dateTimeTable: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      },
    },
    nl: {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      },
      monthLong: {
        month: 'long',
      },
      monthDay: {
        month: 'short',
        day: '2-digit',
      },
      monthDayLong: {
        month: 'long',
        day: 'numeric',
      },
      monthDayYearLong: {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      },
      weekDayShort: {
        weekday: 'short',
      },
      weekDayAndDay: {
        weekday: 'short',
        day: 'numeric',
      },
      shortWithWeekDay: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
      },
      shortText: {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        weekday: 'short',
      },
      shortTextMonthYear: {
        year: 'numeric',
        month: 'short',
      },
      long: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
      },
      longDate: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
      dateTime: {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
      dateTimeTable: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
    },
  },
  fallbackLocale: DEFAULT_LOCALE,
  locale: getLocale(),
  missingWarn: false, // Disable warnings for missing keys
  fallbackWarn: false, // Disable warnings for fallback keys
  messages: merge(
    { nl: globalsNL, en: globalsEN },
    { nl: roleNamesNL, en: roleNamesEN },
    { nl: humanDurationNL, en: humanDurationEN },
    { nl: knownErrorNL, en: knownErrorEN },
    { nl: rapportenNL, en: rapportenEN },
    { nl: validatorMessagesNL, en: validatorMessagesEN },
    { nl: vangnetNL, en: vangnetEN },
    { nl: werknemerMessagesNL, en: werknemerMessagesEN },
  ),
};

const i18n = createI18n(
  i18nSettings as unknown as Parameters<typeof createI18n>[0],
);
const globalLocale = i18n.global.locale as unknown as Ref;

const localeExists = (locale: string): boolean =>
  i18n.global.availableLocales.includes(locale);

export const setLocale = (locale: Locale): void => {
  if (localeExists(locale)) {
    if (globalLocale.value === locale) {
      return;
    }
    globalLocale.value = locale;
    return;
  }
  // eslint-disable-next-line no-console
  console.log(
    `"${locale}" is not in available locales: ${i18n.global.availableLocales.join(
      ', ',
    )}.`,
  );
};

watch(
  routeQuery,
  ({ locale }) => {
    setLocale(
      typeof locale === 'string' && localeExists(locale)
        ? (locale as Locale)
        : getLocale(),
    );
  },
  { immediate: true },
);

if (import.meta.env.MODE === 'development') {
  (window as unknown as Record<string, unknown>).setI18nLocale = setLocale;
}

export default i18n;
